import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "right-panel" }
const _hoisted_2 = { class: "action-buttons" }
const _hoisted_3 = { class: "split-button-container" }
const _hoisted_4 = { class: "dropdown-item-content" }
const _hoisted_5 = { class: "dropdown-item-description" }
const _hoisted_6 = { class: "shareable-table-wrapper" }
const _hoisted_7 = ["slot"]
const _hoisted_8 = ["slot"]
const _hoisted_9 = ["slot"]
const _hoisted_10 = { class: "settings-section settings" }
const _hoisted_11 = ["slot"]
const _hoisted_12 = ["href"]
const _hoisted_13 = { class: "settings-section" }
const _hoisted_14 = { class: "settings-section" }
const _hoisted_15 = { class: "settings-section" }
const _hoisted_16 = { class: "settings-section" }
const _hoisted_17 = { class: "settings-section" }
const _hoisted_18 = ["slot"]
const _hoisted_19 = ["href"]
const _hoisted_20 = { class: "settings-section" }
const _hoisted_21 = ["slot"]
const _hoisted_22 = ["href"]

import { ref, computed, onMounted, watch } from "vue";
import type { FiBuild, ModuleInfo } from "../types/build";
import { useAuthStore } from "../stores/authStore";
import { logger } from "../utils/logger";
import { generatePublicDownloadLink, listShareableUrls, revokeShareableUrl } from "../services/api";

interface Props {
  buildId: string;
  build: object | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'buildDetailsRight',
  props: {
    buildId: {},
    build: {}
  },
  emits: ["download-app", "download-app-bundle", "download-archive", "delete-build", "undelete-build"],
  setup(__props: any, { emit: __emit }) {

const authStore = useAuthStore();

const complianceLogoLabels: Record<string, string> = {
  "1": "FDIC",
  "2": "NCUA",
};
const complianceLogoLabel = (val?: string) => complianceLogoLabels[val ?? ""] ?? "N/A";
const linkGenerated = ref(false);
const generatingLink = ref(false);
const showPublicLinkMessage = ref(false);

// Shareable URLs
const shareableUrls = ref([]);
const loadingShareableUrls = ref(false);
const copiedShareableUrlId = ref<string | null>(null);

// Device detection
const isMobile = computed(() => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
});

const isIOS = computed(() => {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
});

const isAndroid = computed(() => {
  return /Android/i.test(navigator.userAgent);
});

const downloadButtonText = computed(() => {
  return isMobile.value ? "Install App" : "Download App";
});

const shareableUrlsHeaders = ref([
  { title: "Link", key: "link" },
  { title: "Expires", key: "expires" },
  { title: "Views", key: "views" },
  { title: "", key: "actions" },
]);

const shareableUrlsRows = computed(() => {
  if (!shareableUrls.value || shareableUrls.value.length === 0) return [];

  const baseUrl = process.env.VUE_APP_BASE_URL || window.location.origin;
  const build = resolvedBuild.value;
  const fiNumber = build?.fiNumber || props.buildId.split("-")[0];

  return shareableUrls.value.map((url, index) => ({
    id: `share-${index}`,
    cells: {
      link: `${baseUrl}/s/${fiNumber}/${url.shortKey}`,
      expires: formatTimestamp(url.expiresAt),
      expiresAt: url.expiresAt, // ISO string for q2-relative-time
      views: url.accessCount || 0,
      actions: "",
      shortKey: url.shortKey, // For delete action
    },
  }));
});

const props = __props;

const buildSettingHeaders = ref([
  { title: "Build Setting", key: "setting" },
  { title: "", key: "value" },
]);

const osSettingsHeaders = ref([
  { title: "OS Settings", key: "setting" },
  { title: "", key: "value" },
]);

const featuresHeaders = ref([
  { title: "Features", key: "feature" },
  { title: "Status", key: "value" },
]);

const devBetaHeaders = ref([
  { title: "Dev/Beta Features", key: "feature" },
  { title: "Status", key: "status" },
]);

const installedModulesHeaders = ref([
  { title: "Installed Modules", key: "module" },
  { title: "Version", key: "version" },
  { title: "Route", key: "route" },
]);

const appLinkHeaders = ref([
  { title: "App Link Settings", key: "setting" },
  { title: "", key: "value" },
]);


const universalLinkHeaders = ref([
  { title: "Settings", key: "setting" },
  { title: "", key: "value" },
]);


const resolvedBuild = computed<FiBuild | null>(() => {
  if (!props.buildId || !props.build) return null;

  return props.build;
});

const applicationURL = computed(() => {
  const b: Partial<FiBuild> = resolvedBuild.value || {};

  return b.buildSettings.targetURLBase + "/" + b.buildSettings.targetPageName;
});

const linkSlotID = computed(() => {
  const baseId = props.buildId || "unknown";

  return "row-" + baseId + "-3-cell-value";
});

const wellKnownURL = computed(() => {
  const b: Partial<FiBuild> = resolvedBuild.value || {};

  return "/v1/build/" + b.buildId + "/download?type=well-known";
});

const wellKnownSlotID = computed(() => {
  const baseId = props.buildId || "unknown";

  return "row-" + baseId + "-link-1-cell-value";
});

function formatTimestamp(isoString) {
  if (!isoString) return "N/A";
  const date = new Date(isoString);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

const buildSettingRows = computed(() => {
  const b: Partial<FiBuild> = resolvedBuild.value || {};
  const baseId = props.buildId || "unknown";
  return [
    {
      id: `${baseId}-0`,
      cells: {
        setting: "App Name",
        value: b.buildSettings.applicationName || "N/A",
      },
    },
    {
      id: `${baseId}-1`,
      cells: {
        setting: "Application Version",
        value: b.buildSettings.containerVersion || "N/A",
      },
    },
    {
      id: `${baseId}-2`,
      cells: {
        setting: "Build Time",
        value: formatTimestamp(b.buildSettings.lastBuild) || "N/A",
      },
    },
    {
      id: `${baseId}-3`,
      cells: {
        setting: "Application URL",
        value: b.buildSettings.targetURLBase + "/" + b.buildSettings.targetPageName || "N/A",
      },
    },
    {
      id: `${baseId}-4`,
      cells: { setting: "Build Branch", value: b.buildSettings.codeBranch || "N/A" },
    },
    {
      id: `${baseId}-5`,
      cells: {
        setting: "Build Reason",
        value: b.buildReason || "N/A",
      },
    },
  ];
});

const androidSettingsRows = computed(() => {
  const b: Partial<FiBuild> = resolvedBuild.value || {};
  const baseId = props.buildId || "unknown";
  /* eslint-disable */
  const tickIfTrue = (val?: boolean | string | any[]) => {
    if (val === true) return "✓"; // strictly true
    if (typeof val === "string") return val.length > 0 ? "✓" : "✕";
    if (Array.isArray(val)) return val.length > 0 ? "✓" : "✕";
    if (val === false) return "✕";
    return "✕";
  };

  return [
    {
      id: `${baseId}-a-0`,
      cells: { setting: "Package", value: b.buildSettings.package || "N/A" },
    },
    {
      id: `${baseId}-a-1`,
      cells: {
        setting: "Contacts for UUX/Tecton",
        value: tickIfTrue(b.buildSettings.contacts),
      },
    },
    {
      id: `${baseId}-a-2`,
      cells: { setting: "Framework Version", value: b.version || "N/A" },
    }, // idk if this is right
    {
      id: `${baseId}-a-3`,
      cells: {
        setting: "Third Party App Package Name(s)",
        value: b.buildSettings.packageNameQueries || "N/A",
      },
    },
    {
      id: `${baseId}-a-4`,
      cells: {
        setting: "Google Cloud API Key",
        value: tickIfTrue(b.buildSettings.googleCloudApiKey),
      },
    },
    {
      id: `${baseId}-a-5`,
      cells: {
        setting: "Version Offset",
        value: b.buildSettings.androidVersionCodeOffset,
      },
    },
    {
      id: `${baseId}-a-6`,
      cells: {
        setting: "App Device Integrity (ADI) Registration",
        value: b.buildSettings.adiRegistration || "N/A",
      },
    },
  ];
});

const iosSettingsRows = computed(() => {
  const b: Partial<FiBuild> = resolvedBuild.value || {};

  /* eslint-disable */
  const tickIfTrue = (val?: boolean | string | any[]) => {
    if (val === true) return '✓'; // strictly true
    if (typeof val === 'string') return val.length > 0 ? '✓' : '✕';
    if (Array.isArray(val)) return val.length > 0 ? '✓' : '✕';
    if (val === false) return '✕';
    return '✕';
  };

  const baseId = props.buildId || 'unknown';
  return [
    { id: `${baseId}-ios-0`, cells: { setting: 'Application Bundle Identifier', value: b.buildSettings.bundleIdentifier || 'N/A' } },
    { id: `${baseId}-ios-1`, cells: { setting: 'Application Bundle Identifier Prefix', value: b.buildSettings.bundleIdentifierPrefix || 'N/A' } },
    { id: `${baseId}-ios-2`, cells: { setting: 'App Store Version Number', value: b.buildSettings.appStoreVersion || 'N/A' } },
    { id: `${baseId}-ios-3`, cells: { setting: 'Application Bundle Version', value: b.buildSettings.bundleVersion || 'N/A' } },
    { id: `${baseId}-ios-4`, cells: { setting: 'URL Schemes', value: b.buildSettings.urlScheme || 'N/A' } },
    { id: `${baseId}-ios-5`, cells: { setting: 'Apple Watch', value: tickIfTrue(b.buildSettings.appleWatchEnabled) } },
    { id: `${baseId}-ios-6`, cells: { setting: 'Apple Watch API URL', value: b.buildSettings.preLoginBalanceURL || 'N/A' } },
    { id: `${baseId}-ios-7`, cells: { setting: 'Apple Watch Bundle Identifier', value: b.buildSettings.bundleIdentifierWatch || 'N/A' } },
    { id: `${baseId}-ios-8`, cells: { setting: 'Apple Watch Bundle Identifier Extension', value: b.buildSettings.bundleIdentifierWatchExtension || 'N/A' } }
  ];
});

const featuresRows = computed(() => {
  const b: Partial<FiBuild> = resolvedBuild.value || {};
  const baseId = props.buildId || "unknown";
  /* eslint-disable */
  const tickIfTrue = (val?: boolean | string | any[]) => {
    if (val === true) return "✓"; // strictly true
    if (typeof val === "string") return val.length > 0 ? "✓" : "✕";
    if (Array.isArray(val)) return val.length > 0 ? "✓" : "✕";
    if (val === false) return "✕";
    return "✕";
  };

  return [
    {
      id: `${baseId}-f-1`,
      cells: {
        feature: "Native Login Footer Text",
        value: tickIfTrue(b.buildSettings.loginFooter),
      },
    },
    {
      id: `${baseId}-f-2`,
      cells: { feature: "Native Menu", value: tickIfTrue(b.buildSettings.nativeMenu) },
    },
    {
      id: `${baseId}-f-3`,
      cells: {
        feature: "Native Enroll: Four Digit Pin / Touch ID",
        value: tickIfTrue(b.buildSettings.q2configTouchEnrollment),
      },
    },
    {
      id: `${baseId}-f-4`,
      cells: { feature: "Native mRDC", value: tickIfTrue(b.buildSettings.nativeMrdc) },
    },
    {
      id: `${baseId}-f-5`,
      cells: { feature: "Vertifi Camera", value: tickIfTrue(b.buildSettings.vertifi) },
    },
    {
      id: `${baseId}-f-6`,
      cells: { feature: "Push Notifications", value: tickIfTrue(b.buildSettings.push) },
    },
    {
      id: `${baseId}-f-7`,
      cells: { feature: "App Ratings", value: tickIfTrue(b.buildSettings.appRatings) },
    },
    {
      id: `${baseId}-f-8`,
      cells: {
        feature: "Compliance Logo and Text",
        value: complianceLogoLabel(b.buildSettings.complianceLogo),
      },
    },
    {
      id: `${baseId}-f-10`,
      cells: {
        feature: "(LA) Hide Overbrowser URL",
        value: tickIfTrue(b.buildSettings.hideOverBrowserUrl),
      },
    },
    {
      id: `${baseId}-f-11`,
      cells: {
        feature: "Age Signals",
        value: tickIfTrue(b.buildSettings.useAgeServiceAPI),
      },
    },
    {
      id: `${baseId}-f-12`,
      cells: {
        feature: "Passkey Support for 3rd Party IDP",
        value: tickIfTrue(b.buildSettings.passkeySupport),
      },
    },
  ];
});

const devBetaRows = computed(() => {
  const b: Partial<FiBuild> = resolvedBuild.value || {};
  const baseId = props.buildId || "unknown";
  const rows = [];
  /* eslint-disable */
  const tickIfTrue = (val?: boolean | string | any[]) => {
    if (val === true) return "✓"; // strictly true
    if (typeof val === "string") return val.length > 0 ? "✓" : "✕";
    if (Array.isArray(val)) return val.length > 0 ? "✓" : "✕";
    if (val === false) return "✕";
    return "✕";
  };

  if (b.buildSettings.enableDevMode !== undefined)
    rows.push({
      id: `${baseId}-devmode`,
      cells: {
        feature: "Dev Settings",
        status: tickIfTrue(b.buildSettings.enableDevMode),
      },
    });
  if (b.buildSettings.deviceMicEnabled !== undefined)
    rows.push({
      id: `${baseId}-mic`,
      cells: {
        feature: "Microphone permissions enabled",
        status: tickIfTrue(b.buildSettings.deviceMicEnabled),
      },
    });
  return rows;
});

const installedModulesRows = computed(() => {
  const modules: ModuleInfo[] = resolvedBuild.value?.buildSettings?.modules || [];

  const baseId = props.buildId || "unknown";
  return modules
    .filter((m) => m.enabled && !m.name?.toLowerCase().includes("internal"))
    .map((m, index) => ({
      id: `${baseId}-mod-${index}`,
      cells: {
        module: m.name || m.identifier || "Unknown Module",
        version: m.version || "N/A",
        route: m.route || m.classPath || "N/A",
      },
    }));
});

const appLinkRows = computed(() => {
  const b: Partial<FiBuild> = resolvedBuild.value || {};
  const baseId = props.buildId || "unknown";
  return [
    {
      id: `${baseId}-link-0`,
      cells: {
        setting: "Associated Domain",
        value: b.buildSettings.universalAndAppLinkAssociatedDomain || "N/A",
      },
    },
    {
      id: `${baseId}-link-1`,
      cells: { setting: "Well-Known File", value: "assetlinks.json" },
    },
  ];
});

const universalLinkRows = computed(() => {
  const b: Partial<FiBuild> = resolvedBuild.value || {};

  const baseId = props.buildId || 'unknown';
  return [
    { id: `${baseId}-link-0`, cells: { setting: 'Associated Domain', value: b.buildSettings.universalAndAppLinkAssociatedDomain || 'N/A' } },
    { id: `${baseId}-link-1`, cells: { setting: 'Well-Known File', value: 'apple-app-site-association' } }
  ];
});



/* global defineEmits */
const emit = __emit;

// The API stamps `markedForDeletion` (a timestamp) on soft delete; there is no
// `markedToDelete` field on the wire, so the old name was always undefined.
const isMarkedForDeletion = computed(() => {
  return resolvedBuild.value?.markedForDeletion != null;
});

async function downloadApp() {
  logger.debug("📦 Download App clicked");

  const build = resolvedBuild.value;
  const buildPlatform = build?.platform?.toLowerCase();

  // If on mobile iOS and build is iOS, use sideload for OTA installation
  if (isMobile.value && isIOS.value && buildPlatform === 'ios') {
    logger.debug("📱 iOS mobile device detected, triggering OTA installation");
    try {
      // Generate a shareable public link for the sideload manifest (no auth required)
      const response = await generatePublicDownloadLink(props.buildId, 'manifest');

      const fiNumber = build?.fiNumber || props.buildId.split("-")[0];
      const shortKey = response.shortKey;

      // The /v1/s endpoint is unauthenticated, so target the API host directly rather
      // than relying on the website's nginx proxy. iOS itms-services requires an absolute URL.
      const apiUrl = process.env.VUE_APP_API_URL;
      if (!apiUrl) {
        logger.error("VUE_APP_API_URL is not set; cannot build iOS manifest URL");
        alert("Configuration error: API URL is not set.");
        return;
      }
      const manifestUrl = `${apiUrl}/v1/s/${fiNumber}/${shortKey}`;

      // Create iOS OTA install URL - iOS is picky about encoding, only encode special chars
      // Don't use encodeURIComponent as it encodes : and / which breaks the URL
      const encodedUrl = manifestUrl.replace(/&/g, '%26').replace(/\?/g, '%3F').replace(/#/g, '%23');
      const installUrl = `itms-services://?action=download-manifest&url=${encodedUrl}`;

      logger.debug("📱 Manifest URL:", manifestUrl);
      logger.debug("🔗 Encoded URL:", encodedUrl);
      logger.debug("🔗 Install URL:", installUrl);

      // Trigger iOS OTA installation
      window.location.href = installUrl;
    } catch (err) {
      logger.error("❌ Failed to trigger iOS installation:", err);
      alert("Failed to initiate installation. Please try downloading instead.");
    }
    return;
  }

  // If on mobile Android and build is Android, download APK
  if (isMobile.value && isAndroid.value && buildPlatform === 'android') {
    logger.debug("📱 Android mobile device detected, downloading APK");
    emit("download-app");
    return;
  }

  // Desktop or other scenarios - use regular download
  emit("download-app");
}

function downloadAppBundle() {
  logger.debug("📦 Download App Bundle clicked");
  emit("download-app-bundle");
}

function downloadArchive() {
  logger.debug("📦 Download Archive clicked");
  emit("download-archive");
}

async function generatePublicLink() {
  // Show confirmation dialog
  const confirmed = window.confirm(
      "Are you sure you want to generate a public download link and copy it to your clipboard?\n\n" +
      "This link will be valid for 7 days and can be shared with anyone. You can remove public access at any time by deleting the link under the public links section."
  );

  if (!confirmed) {
    logger.debug("User cancelled public link generation");
    return;
  }

  generatingLink.value = true;
  try {
    const build = resolvedBuild.value;
    const downloadType = build?.platform === 'android' ? 'app' : 'app'; // Could be 'aab' for Android App Bundle

    const response = await generatePublicDownloadLink(props.buildId, downloadType);

    // Extract fiNumber and shortKey from the API response
    const fiNumber = build?.fiNumber || props.buildId.split('-')[0];
    const shortKey = response.shortKey;

    // Build the shareable URL using the distribution site's base URL
    // Use env variable if present, otherwise fall back to current host
    const baseUrl = process.env.VUE_APP_BASE_URL || window.location.origin;
    const publicUrl = `${baseUrl}/s/${fiNumber}/${shortKey}`;

    if (publicUrl) {
      // Try to copy to clipboard, with fallback for focus issues after confirm dialog
      try {
        await navigator.clipboard.writeText(publicUrl);
        logger.debug("✅ Generated and copied public download link:", publicUrl);
      } catch (clipboardErr) {
        // Fallback: use a temporary textarea if clipboard API fails
        logger.debug("Clipboard API failed, using fallback method");
        const textarea = document.createElement('textarea');
        textarea.value = publicUrl;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        try {
          document.execCommand('copy');
          logger.debug("✅ Generated and copied public download link (fallback):", publicUrl);
        } catch (fallbackErr) {
          logger.error("❌ Failed to copy with fallback method:", fallbackErr);
        }
        document.body.removeChild(textarea);
      }

      logger.debug("Link expires at:", response.expiresAt);
      linkGenerated.value = true;
      setTimeout(() => {
        linkGenerated.value = false;
      }, 2000);

      // Show success message for 5 seconds
      showPublicLinkMessage.value = true;
      setTimeout(() => {
        showPublicLinkMessage.value = false;
      }, 5000);

      // Reload shareable URLs list
      await loadShareableUrls();
    } else {
      logger.error("❌ No download link in response");
    }
  } catch (err) {
    logger.error("❌ Failed to generate public download link:", err);
  } finally {
    generatingLink.value = false;
  }
}

function deleteBuildClick() {
  emit("delete-build");
}
function undeleteBuildClick() {
  logger.debug("♻️ Undelete clicked");
  emit("undelete-build"); // You’ll handle this in the parent
}

// Load shareable URLs when component mounts
onMounted(async () => {
  await loadShareableUrls();
});

// Reload shareable URLs when buildId changes
watch(() => props.buildId, async (newBuildId) => {
  if (newBuildId) {
    await loadShareableUrls();
  }
});

async function loadShareableUrls() {
  if (!props.buildId) return;

  loadingShareableUrls.value = true;
  try {
    const response = await listShareableUrls(props.buildId);
    shareableUrls.value = response.shares || [];
    logger.debug("✅ Loaded shareable URLs:", shareableUrls.value);
  } catch (err) {
    logger.error("❌ Failed to load shareable URLs:", err);
    shareableUrls.value = [];
  } finally {
    loadingShareableUrls.value = false;
  }
}

async function deleteShareableUrl(shortKey) {
  const confirmed = window.confirm("Are you sure you want to revoke this shareable link?");
  if (!confirmed) return;

  try {
    const build = resolvedBuild.value;
    const fiNumber = build?.fiNumber || props.buildId.split("-")[0];

    await revokeShareableUrl(fiNumber, shortKey);
    logger.debug("✅ Revoked shareable URL:", shortKey);

    // Reload the list
    await loadShareableUrls();
  } catch (err) {
    logger.error("❌ Failed to revoke shareable URL:", err);
    alert("Failed to revoke shareable link");
  }
}

function copyShareableUrl(url, shortKey) {
  navigator.clipboard.writeText(url)
    .then(() => {
      logger.debug("✅ Copied shareable URL:", url);
      copiedShareableUrlId.value = shortKey;
      setTimeout(() => {
        copiedShareableUrlId.value = null;
      }, 5000);
    })
    .catch((err) => {
      logger.error("❌ Failed to copy URL:", err);
    });
}

return (_ctx: any,_cache: any) => {
  const _component_q2_icon = _resolveComponent("q2-icon")!
  const _component_q2_btn = _resolveComponent("q2-btn")!
  const _component_q2_dropdown_item = _resolveComponent("q2-dropdown-item")!
  const _component_q2_dropdown = _resolveComponent("q2-dropdown")!
  const _component_q2_message = _resolveComponent("q2-message")!
  const _component_q2_relative_time = _resolveComponent("q2-relative-time")!
  const _component_q2_data_table = _resolveComponent("q2-data-table")!
  const _component_q2_section = _resolveComponent("q2-section")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (!isMarkedForDeletion.value && _unref(authStore).isAdmin)
        ? (_openBlock(), _createBlock(_component_q2_btn, {
            key: 0,
            intent: "workflow-destroy",
            class: "icon-only-btn",
            onClick: _cache[0] || (_cache[0] = () => deleteBuildClick())
          }, {
            default: _withCtx(() => [
              _createVNode(_component_q2_icon, { type: "trash" })
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (_unref(authStore).isAdmin && isMarkedForDeletion.value)
        ? (_openBlock(), _createBlock(_component_q2_btn, {
            key: 1,
            intent: "workflow-secondary",
            onClick: _cache[1] || (_cache[1] = () => undeleteBuildClick())
          }, {
            default: _withCtx(() => [
              _cache[2] || (_cache[2] = _createTextVNode(" Restore ", -1)),
              _createVNode(_component_q2_icon, { type: "refresh" })
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_q2_btn, {
          intent: "workflow-primary",
          class: "split-button-main",
          size: "medium",
          onClick: downloadApp
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(downloadButtonText.value), 1)
          ]),
          _: 1
        }),
        _createVNode(_component_q2_dropdown, {
          type: "primary",
          icon: "chevron-down",
          "popover-alignment": "right",
          class: "split-button-dropdown"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_q2_dropdown_item, {
              value: "share",
              removable: "false",
              onClick: generatePublicLink
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_4, [
                  _cache[3] || (_cache[3] = _createElementVNode("div", { class: "dropdown-item-title" }, "Generate Public Install Link", -1)),
                  _createElementVNode("div", _hoisted_5, _toDisplayString(__props.build?.platform === 'android' ? 'Create .apk install link valid for 7 days' : 'Create .ipa install link valid for 7 days'), 1)
                ])
              ]),
              _: 1
            }),
            (__props.build?.platform == 'android')
              ? (_openBlock(), _createBlock(_component_q2_dropdown_item, {
                  key: 0,
                  value: "aab",
                  removable: "false",
                  onClick: downloadAppBundle
                }, {
                  default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                    _createElementVNode("div", { class: "dropdown-item-content" }, [
                      _createElementVNode("div", { class: "dropdown-item-title" }, "Download App Bundle"),
                      _createElementVNode("div", { class: "dropdown-item-description" }, "Download AAB file for Play Store submission")
                    ], -1)
                  ]))]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (_unref(authStore).isAdmin)
              ? (_openBlock(), _createBlock(_component_q2_dropdown_item, {
                  key: 1,
                  value: "archive",
                  removable: "false",
                  onClick: downloadArchive
                }, {
                  default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                    _createElementVNode("div", { class: "dropdown-item-content" }, [
                      _createElementVNode("div", { class: "dropdown-item-title" }, "Download Build Archive"),
                      _createElementVNode("div", { class: "dropdown-item-description" }, "Download complete build files")
                    ], -1)
                  ]))]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ])
    ]),
    (showPublicLinkMessage.value)
      ? (_openBlock(), _createBlock(_component_q2_message, {
          key: 0,
          type: "success",
          appearance: "standard",
          description: "true",
          class: "public-link-message"
        }, {
          default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
            _createTextVNode(" A public link has been generated and copied to your clipboard! ", -1)
          ]))]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (shareableUrls.value.length > 0)
      ? (_openBlock(), _createBlock(_component_q2_section, {
          key: 1,
          label: `Public Shareable Links (${shareableUrls.value.length})`,
          collapsible: "",
          class: "shareable-urls-section"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_6, [
              _createVNode(_component_q2_data_table, {
                class: "setting-section-table",
                ".headers": shareableUrlsHeaders.value,
                ".rows": shareableUrlsRows.value,
                "empty-icon": "inbox",
                "empty-message": "No public links",
                striped: "true"
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(shareableUrlsRows.value, (row) => {
                    return (_openBlock(), _createElementBlock(_Fragment, {
                      key: row.id
                    }, [
                      _createElementVNode("div", {
                        slot: `row-${row.id}-cell-link`
                      }, [
                        _createVNode(_component_q2_btn, {
                          intent: "workflow-primary",
                          onClick: ($event: any) => (copyShareableUrl(row.cells.link, row.cells.shortKey)),
                          title: row.cells.link,
                          disabled: copiedShareableUrlId.value === row.cells.shortKey
                        }, {
                          default: _withCtx(() => [
                            (copiedShareableUrlId.value !== row.cells.shortKey)
                              ? (_openBlock(), _createBlock(_component_q2_icon, {
                                  key: 0,
                                  type: "copy"
                                }))
                              : _createCommentVNode("", true),
                            (copiedShareableUrlId.value === row.cells.shortKey)
                              ? (_openBlock(), _createBlock(_component_q2_icon, {
                                  key: 1,
                                  type: "todo"
                                }))
                              : _createCommentVNode("", true),
                            _createTextVNode(" " + _toDisplayString(copiedShareableUrlId.value === row.cells.shortKey ? 'Copied' : row.cells.shortKey), 1)
                          ]),
                          _: 2
                        }, 1032, ["onClick", "title", "disabled"])
                      ], 8, _hoisted_7),
                      _createElementVNode("div", {
                        slot: `row-${row.id}-cell-expires`
                      }, [
                        _createElementVNode("div", null, _toDisplayString(row.cells.expires), 1),
                        _createVNode(_component_q2_relative_time, {
                          date: row.cells.expiresAt,
                          "message-format": "short",
                          class: "relative-time",
                          sync: "true"
                        }, null, 8, ["date"])
                      ], 8, _hoisted_8),
                      _createElementVNode("div", {
                        slot: `row-${row.id}-cell-actions`
                      }, [
                        _createVNode(_component_q2_btn, {
                          intent: "workflow-destroy",
                          class: "icon-only-btn small-btn",
                          title: "Revoke link",
                          onClick: ($event: any) => (deleteShareableUrl(row.cells.shortKey))
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_q2_icon, { type: "trash" })
                          ]),
                          _: 1
                        }, 8, ["onClick"])
                      ], 8, _hoisted_9)
                    ], 64))
                  }), 128))
                ]),
                _: 1
              }, 40, [".headers", ".rows"])
            ])
          ]),
          _: 1
        }, 8, ["label"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_10, [
      (buildSettingRows.value)
        ? (_openBlock(), _createBlock(_component_q2_data_table, {
            key: 0,
            class: "setting-section-table",
            ".headers": buildSettingHeaders.value,
            ".rows": buildSettingRows.value,
            "empty-icon": "inbox",
            "empty-message": "Nothing to display",
            striped: "true"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", { slot: linkSlotID.value }, [
                _createElementVNode("a", {
                  href: applicationURL.value,
                  target: "_blank"
                }, _toDisplayString(applicationURL.value), 9, _hoisted_12)
              ], 8, _hoisted_11)
            ]),
            _: 1
          }, 40, [".headers", ".rows"]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_13, [
      (androidSettingsRows.value && __props.build.platform == 'android')
        ? (_openBlock(), _createBlock(_component_q2_data_table, {
            key: 0,
            class: "setting-section-table",
            ".headers": osSettingsHeaders.value,
            ".rows": androidSettingsRows.value,
            "empty-icon": "inbox",
            "empty-message": "Nothing to display",
            striped: "true"
          }, null, 40, [".headers", ".rows"]))
        : _createCommentVNode("", true),
      (iosSettingsRows.value && __props.build.platform == 'ios')
        ? (_openBlock(), _createBlock(_component_q2_data_table, {
            key: 1,
            class: "setting-section-table",
            headers: osSettingsHeaders.value,
            rows: iosSettingsRows.value || [],
            "empty-icon": "inbox",
            striped: "true"
          }, null, 8, ["headers", "rows"]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_14, [
      (featuresRows.value)
        ? (_openBlock(), _createBlock(_component_q2_data_table, {
            key: 0,
            class: "setting-section-table",
            ".headers": featuresHeaders.value,
            ".rows": featuresRows.value,
            "empty-icon": "inbox",
            "empty-message": "Nothing to display",
            striped: "true"
          }, null, 40, [".headers", ".rows"]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_15, [
      (devBetaRows.value)
        ? (_openBlock(), _createBlock(_component_q2_data_table, {
            key: 0,
            class: "setting-section-table",
            ".headers": devBetaHeaders.value,
            ".rows": devBetaRows.value,
            "empty-icon": "inbox",
            "empty-message": "Nothing to display",
            striped: "true"
          }, null, 40, [".headers", ".rows"]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_16, [
      (installedModulesRows.value)
        ? (_openBlock(), _createBlock(_component_q2_data_table, {
            key: 0,
            class: "setting-section-table",
            ".headers": installedModulesHeaders.value,
            ".rows": installedModulesRows.value,
            "empty-icon": "inbox",
            "empty-message": "Nothing to display",
            striped: "true"
          }, null, 40, [".headers", ".rows"]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_17, [
      (appLinkRows.value && __props.build.platform == 'android')
        ? (_openBlock(), _createBlock(_component_q2_data_table, {
            key: 0,
            class: "setting-section-table",
            ".headers": appLinkHeaders.value,
            ".rows": appLinkRows.value,
            "empty-icon": "inbox",
            "empty-message": "Nothing to display",
            striped: "true"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", { slot: wellKnownSlotID.value }, [
                _createElementVNode("a", {
                  href: wellKnownURL.value,
                  target: "_blank"
                }, "assetlinks.json", 8, _hoisted_19)
              ], 8, _hoisted_18)
            ]),
            _: 1
          }, 40, [".headers", ".rows"]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_20, [
      (universalLinkRows.value && __props.build.platform == 'ios')
        ? (_openBlock(), _createBlock(_component_q2_data_table, {
            key: 0,
            class: "setting-section-table",
            ".headers": universalLinkHeaders.value,
            ".rows": universalLinkRows.value,
            "empty-icon": "inbox",
            "empty-message": "Nothing to display",
            striped: "true"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", { slot: wellKnownSlotID.value }, [
                _createElementVNode("a", {
                  href: wellKnownURL.value,
                  target: "_blank"
                }, "apple-app-site-association", 8, _hoisted_22)
              ], 8, _hoisted_21)
            ]),
            _: 1
          }, 40, [".headers", ".rows"]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})